import type { Dispatch } from 'redux';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import { useCallback } from 'react';

import { Button } from 'UIPackage/MainModule/components/Button';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { monthOptions } from 'CorePackage/MainModule/constants/monthOptions';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import {
  getAccountKnoOrganizationsSelectOptions,
  getAccountProsecutorOrganizationsSelectOptions,
} from 'CorePackage/AccountModule/selectors/accountSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnoOrganizationDictionary } from 'APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { getDictionariesKnoOrganizationsSelectOptions } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';

import styles from './SimilaritiesFiltersModal.module.scss';
import { nextYear } from '../constants/nextYear';
import { similaritiesGetData, similaritiesSetFilters } from '../actions/similaritiesActions';

const emptySelect = { id: '', label: '', value: '' };

const yearOption = [
  {
    id: nextYear,
    label: nextYear,
    value: nextYear,
  },
];

interface ISimilaritiesFiltersModalProps {
  isVisible?: boolean;
  onClose: () => void;
  version: KnmVersionsEnum;
}

interface IFiltersModalForm {
  year: ISelectOption;
  month: ISelectOption;
  prosecutorId: IUserOrganisationSelectOption;
  includeChildProsec: boolean;
  knoId: ISelectOption;
  notProcessed: boolean;
}

interface ISimilaritiesFiltersModalStoreProps {
  userKnoOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  userProsecutorOrganisationsSelectOptions: IUserOrganisationSelectOption[];
  erknmOrganisationsSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
}

const SimilaritiesFiltersModalWithoutStore = ({
  isVisible,
  onClose,
  version,
  userKnoOrganisationsSelectOptions,
  userProsecutorOrganisationsSelectOptions,
  erknmOrganisationsSelectOptions,
}: ISimilaritiesFiltersModalProps & ISimilaritiesFiltersModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const prosecutorByDefault = userProsecutorOrganisationsSelectOptions.filter(
    (org: { byDefault?: boolean }) => org.byDefault,
  )[0];

  const onSubmit = useCallback(
    (data: IFiltersModalForm) => {
      const filter = {
        year: Number(data?.year?.id) === 0 ? undefined : Number(data?.year?.id),
        month: Number(data?.month?.id) === 0 ? undefined : Number(data?.month?.id),
        prosecutorId: Number(data?.prosecutorId?.id) === 0 ? undefined : Number(data?.prosecutorId?.id),
        includeChildProsec: data?.includeChildProsec,
        knoId: Number(data?.knoId?.id) === 0 ? undefined : Number(data?.knoId?.id),
        knoErvkId: data?.knoId?.value?.dictVersionId,
        notProcessed: data?.notProcessed,
        version,
      };

      dispatch(similaritiesSetFilters(filter));
      dispatch(similaritiesGetData());
      onClose();
    },
    [dispatch, onClose, version],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<IFiltersModalForm>) => (
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Фильтры" color="black" />
        <ModalBody size="content" border="bottom">
          <Row>
            <FieldBlock label="Год сводного плана" color="black">
              <SelectInputField name="year" options={yearOption} />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Месяц" color="black">
              <SelectInputField name="month" options={monthOptions} />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Прокуратура" color="black">
              <SelectInputField name="prosecutorId" options={userProsecutorOrganisationsSelectOptions} disabled />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock>
              <CheckboxField
                id="includeChildProsec"
                theme="system"
                size="medium"
                name="includeChildProsec"
                label="С учетом подчиненных прокуратур"
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Наименование органа контроля" color="black">
              <SelectInputField name="knoId" options={erknmOrganisationsSelectOptions} />
            </FieldBlock>
          </Row>
          <Row margin="none">
            <FieldBlock>
              <CheckboxField
                id="notProcessed"
                theme="system"
                size="medium"
                name="notProcessed"
                label="Только необработанные совпадения"
              />
            </FieldBlock>
          </Row>
        </ModalBody>
        <ModalActions>
          <Button type="submit" color="primary" size="medium" text="Применить" />
          <Button type="button" onClick={onClose} color="default" size="medium" text="Отменить" />
        </ModalActions>
      </form>
    ),
    [
      onClose,
      userKnoOrganisationsSelectOptions,
      userProsecutorOrganisationsSelectOptions,
      erknmOrganisationsSelectOptions,
    ],
  );

  return (
    <Modal modalBodyClassName={styles.Modal} visible={isVisible} onClose={onClose}>
      <Form<IFiltersModalForm>
        keepDirtyOnReinitialize
        initialValues={{
          includeChildProsec: false,
          notProcessed: false,
          year: emptySelect,
          month: emptySelect,
          prosecutorId: prosecutorByDefault,
          knoId: emptySelect,
        }}
        onSubmit={onSubmit}
        render={renderForm}
      />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): ISimilaritiesFiltersModalStoreProps => ({
  userKnoOrganisationsSelectOptions: getAccountKnoOrganizationsSelectOptions(state),
  userProsecutorOrganisationsSelectOptions: getAccountProsecutorOrganizationsSelectOptions(state),
  erknmOrganisationsSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
});

export const SimilaritiesFiltersModal = connect(mapStateToProps)(SimilaritiesFiltersModalWithoutStore);
